import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../offline/profileApi';
import { isProfileQueued } from '../offline/profileActions';
import { cacheOwner } from '../offline/profileCache';
import { Alert, Button, Checkbox, Input, InputNumber, List, Modal, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { deleteField, doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import API from '../config/api';
import { useOwnerContext } from './OwnerContext';

const { Text } = Typography;
type InsuranceProfileUpdate = { insurance_company_id: string; insurance_company_name: string; insurance_number: string; coverage_percentage: number };
type Props = { open: boolean; patient: any; doctorId: string; ownerDoctorId: string; clinicId: string; billings: any[]; onClose: () => void; onSaved: (result: any) => Promise<void> | void; onPatientInsuranceUpdated?: (update: InsuranceProfileUpdate) => void };
const itemKey = (item: any) => `${item.itemType || 'visit_type'}:${item.itemId || item.serviceId || item.visitTypeId}`;
const itemId = (item: any) => item.itemId || item.serviceId || item.visitTypeId || itemKey(item);
const itemName = (item: any) => item.serviceName || item.visitTypeName || item.itemName || String(itemId(item));
const isInsurancePackage = (pkg: any) => pkg?.tracking_type === 'insurance' || String(pkg?.package_name || '').startsWith('تأمين -');
const isEditableInsuranceBilling = (billing: any) => ['pending', 'partial'].includes(
  String(billing?.paymentStatus || billing?.payment_status || '').toLowerCase()
);
const waitingListDateKeys = (patient: any) => {
  const keys: string[] = [];
  for (const value of [patient?.date, patient?.last_visit_date]) {
    const match = String(value || '').match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) continue;
    const [, year, month, day] = match;
    keys.push(`${year}-${Number(month)}-${Number(day)}`);
    keys.push(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }
  return Array.from(new Set(keys));
};

const InsuranceProgramModal: React.FC<Props> = ({ open, patient, doctorId, ownerDoctorId, clinicId, billings, onClose, onSaved, onPatientInsuranceUpdated }) => {
  const { canManageOwnerFeatures } = useOwnerContext();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [insuranceCompanyId, setInsuranceCompanyId] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');
  const [insuranceCoveragePercentage, setInsuranceCoveragePercentage] = useState<number | null>(null);
  const [priceLists, setPriceLists] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [priceList, setPriceList] = useState<any | null>(null);
  const [activePackages, setActivePackages] = useState<any[]>([]);
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [programVersion, setProgramVersion] = useState(0);
  const [serverBilling, setServerBilling] = useState<any | null>(null);
  const operationRef = useRef<{ fingerprint: string; id: string } | null>(null);
  const effectiveDoctorId = doctorId || patient?.doctor_id || '';
  const catalogDoctorId = ownerDoctorId || effectiveDoctorId;
  const effectiveClinicId = clinicId || patient?.clinic_id || '';
  const patientId = patient?.patient_id || patient?._id || patient?.id || '';
  const coveragePercentage = Number(insuranceCoveragePercentage ?? 0);
  const existingInsuranceBill = useMemo(() => (billings || []).find((bill) => {
    const status = String(bill?.paymentStatus || bill?.payment_status || '').toLowerCase();
    const billCompanyId = String(bill?.insurance_company_id || bill?.insuranceCompanyId || '');
    const sameCompany = billCompanyId
      ? billCompanyId === String(insuranceCompanyId)
      : String(patient?.insurance_company_id || '') === String(insuranceCompanyId);
    return sameCompany && ['pending', 'partial'].includes(status) && (bill?.paymentMethod === 'insurance' || bill?.payment_method === 'insurance' ||
      (bill?.services || []).some((service: any) => service?.insurance_item_type || service?.insurance_full_price != null));
  }), [billings, insuranceCompanyId, patient?.insurance_company_id]);

  useEffect(() => {
    if (!open) return;
    const savedCompanyId = String(patient?.insurance_company_id || '');
    const savedInsuranceNumber = String(patient?.insurance_number || '').trim();
    setInsuranceCompanyId(savedCompanyId);
    setInsuranceNumber(savedInsuranceNumber);
    setInsuranceCoveragePercentage(Number(patient?.coverage_percentage || 0));
    setCompanies([]);
    if (!catalogDoctorId) return;
    let cancelled = false;
    setCompaniesLoading(true);
    axios.get(`${API.BASE_URL}${API.ENDPOINTS.INSURANCE_COMPANIES(catalogDoctorId)}`)
      .then((response) => {
        if (!cancelled) setCompanies(response.data?.success ? (response.data.data || []) : []);
      })
      .catch((err: any) => !cancelled && setError(err?.response?.data?.message || 'فشل تحميل شركات التأمين'))
      .finally(() => !cancelled && setCompaniesLoading(false));
    return () => { cancelled = true; };
  }, [open, catalogDoctorId, patient?.insurance_company_id, patient?.insurance_number, patient?.coverage_percentage]);

  useEffect(() => {
    if (!open) return;
    setError(''); setPriceLists([]); setSelectedPeriod(''); setPriceList(null); setActivePackages([]); setSelected({});
    setProgramVersion(0); setServerBilling(null); operationRef.current = null;
    if (!effectiveClinicId || !insuranceCompanyId || !effectiveDoctorId || !patientId) {
      if (insuranceCompanyId) setError('تعذر تحديد بيانات المريض أو الطبيب أو العيادة');
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.INSURANCE_PRICE_LISTS(effectiveClinicId, insuranceCompanyId)}`),
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(patientId)}`, { params: {
        doctor_id: effectiveDoctorId, clinic_id: effectiveClinicId, patient_phone: patient.patient_phone || '',
        file_number: patient.file_number || patient.file_id || '',
      } }),
    ]).then(([listsResponse, packagesResponse]) => {
      if (cancelled) return;
      const lists = listsResponse.data?.data || [];
      if (!listsResponse.data?.success || !lists.length) throw new Error(listsResponse.data?.message || 'لا توجد قائمة أسعار تأمين لهذه الشركة');
      setPriceLists(lists); setActivePackages(packagesResponse.data?.data || []);
      const saved = patient.insurance_price_list_date || patient.insurance_price_list_year || '';
      const preferred = lists.find((list: any) => String(list.effectiveDate) === String(saved)) ||
        lists.find((list: any) => String(list.year) === String(saved)) || lists[0];
      setSelectedPeriod(String(preferred.effectiveDate || preferred.year));
    }).catch((err: any) => !cancelled && setError(err?.response?.data?.message || err?.message || 'فشل تحميل برنامج التأمين'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, effectiveClinicId, effectiveDoctorId, patientId, insuranceCompanyId, patient?.insurance_price_list_date, patient?.insurance_price_list_year, patient?.patient_phone, patient?.file_number, patient?.file_id]);

  useEffect(() => {
    if (!open || !selectedPeriod || !effectiveClinicId || !insuranceCompanyId || !catalogDoctorId) return;
    const summary = priceLists.find((list: any) => String(list.effectiveDate || list.year) === String(selectedPeriod));
    if (!summary?.priceListId) return;
    let cancelled = false;
    setLoading(true); setError(''); operationRef.current = null;
    Promise.all([
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.INSURANCE_PRICE_LIST(effectiveClinicId, insuranceCompanyId, selectedPeriod)}`, { params: { doctor_id: catalogDoctorId, activeOnly: 'true' } }),
      axios.get(`${API.BASE_URL}/api/insurance/programs/current`, { params: {
        patient_id: patientId, doctor_id: effectiveDoctorId, clinic_id: effectiveClinicId,
        patient_phone: patient.patient_phone || '', patient_file_number: patient.file_number || patient.file_id || '',
        insurance_company_id: insuranceCompanyId, insurance_price_list_id: summary.priceListId,
        billing_id: existingInsuranceBill?.billing_id || '',
      } }),
    ]).then(([priceResponse, stateResponse]) => {
      if (!priceResponse.data?.success || !stateResponse.data?.success) throw new Error('تعذر تحميل بيانات برنامج التأمين');
      if (!cancelled) {
        setPriceList(priceResponse.data.data || null);
        setProgramVersion(Number(stateResponse.data.data?.program_version || 0));
        const currentBilling = stateResponse.data.data?.billing || null;
        // The billings prop can still contain the pre-payment status until the
        // patient view is refreshed. Prefer the freshly loaded server record so
        // a bill paid in the current screen is not reopened as pending.
        setServerBilling(
          currentBilling
            ? (isEditableInsuranceBilling(currentBilling) ? currentBilling : null)
            : (isEditableInsuranceBilling(existingInsuranceBill) ? existingInsuranceBill : null)
        );
      }
    }).catch((err: any) => { if (!cancelled) { setPriceList(null); setError(err?.response?.data?.message || err?.message || 'تعذر تحميل برنامج التأمين'); } })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, selectedPeriod, effectiveClinicId, effectiveDoctorId, patientId, insuranceCompanyId, patient?.patient_phone, patient?.file_number, patient?.file_id, catalogDoctorId, priceLists, existingInsuranceBill]);

  useEffect(() => {
    if (!priceList) return;
    const currentServices = serverBilling?.services || [];
    const next: Record<string, number> = {};
    for (const item of (priceList.items || priceList.visitTypes || [])) {
      const id = itemId(item);
      const billed = currentServices.find((service: any) => String(service.source_item_id || '') === String(id));
      if (billed) next[itemKey(item)] = Number(billed.insurance_session_count ?? billed.quantity ?? 1) || 1;
    }
    setSelected(next);
  }, [priceList, serverBilling]);

  const insuranceItems = useMemo(() => (priceList?.items || priceList?.visitTypes || []).filter((item: any) => item.insurancePrice != null), [priceList]);
  const relevantActivePackages = useMemo(() => activePackages.filter((pkg: any) =>
    isInsurancePackage(pkg) &&
    (!pkg.insurance_company_id || String(pkg.insurance_company_id) === String(insuranceCompanyId))
  ), [activePackages, insuranceCompanyId]);
  const selections = useMemo(() => insuranceItems.filter((item: any) => selected[itemKey(item)]).map((item: any) => ({
    item_type: item.itemType || 'visit_type', item_id: String(itemId(item)), sessions_count: Number(selected[itemKey(item)]),
    patient_package_id: relevantActivePackages.find((pkg: any) => String(pkg.source_item_id || '') === String(itemId(item)))?.patient_package_id || '',
  })), [insuranceItems, selected, relevantActivePackages]);
  const previewTotal = insuranceItems.filter((item: any) => selected[itemKey(item)]).reduce((sum: number, item: any) => {
    const full = Number(item.insurancePrice || 0);
    const patientPrice = coveragePercentage > 0 ? Math.round((full * (100 - coveragePercentage)) / 100) : full;
    return sum + patientPrice * Number(selected[itemKey(item)] || 0);
  }, 0);
  const mirrorWaitingList = async (result: any) => {
    const waitingId = patient?.patient_id || patient?.id;
    const dateKeys = waitingListDateKeys(patient);
    if (!waitingId || !effectiveClinicId || !dateKeys.length) return;
    try {
      const packages = result.patient_packages || [];
      const updates: Record<string, any> = {
        pending_insurance_packages: deleteField(),
        pending_insurance_patient_package_ids: packages.length ? packages.map((pkg: any) => pkg.patient_package_id) : deleteField(),
        package_session_summaries: packages.length ? packages.map((pkg: any) => ({ patient_package_id: pkg.patient_package_id, package_name: pkg.package_name, remaining_sessions: pkg.remaining_sessions, total_sessions: pkg.total_sessions })) : deleteField(),
        insurance_price_list_id: result.price_list?.price_list_id || '', insurance_price_list_year: result.price_list?.year ?? null,
        insurance_price_list_date: result.price_list?.effective_date || '',
      };
      for (const dateKey of dateKeys) {
        const ref = doc(getFirestore(), 'clinics', effectiveClinicId, 'waiting_list', dateKey, 'patients', waitingId);
        const snapshot = await getDoc(ref);
        if (!snapshot.exists()) continue;
        const bills = Array.isArray(snapshot.data().bills) ? snapshot.data().bills : [];
        if (result.billing) {
          const found = bills.some((bill: any) => bill.billing_id === result.billing.billing_id);
          updates.bills = found ? bills.map((bill: any) => bill.billing_id === result.billing.billing_id ? result.billing : bill) : [...bills, result.billing];
        }
        await updateDoc(ref, updates);
        return;
      }
    } catch (err) { console.warn('Authoritative insurance program saved; Firestore mirror failed:', err); }
  };

  const mirrorInsuranceProfile = async (update: InsuranceProfileUpdate) => {
    const waitingId = patient?.patient_id || patient?.id;
    const dateKeys = waitingListDateKeys(patient);
    if (!waitingId || !effectiveClinicId || !dateKeys.length) return;
    try {
      for (const dateKey of dateKeys) {
        const ref = doc(getFirestore(), 'clinics', effectiveClinicId, 'waiting_list', dateKey, 'patients', waitingId);
        const snapshot = await getDoc(ref);
        if (!snapshot.exists()) continue;
        await updateDoc(ref, update);
        return;
      }
    } catch (err) { console.warn('Insurance profile saved in MongoDB; Firestore mirror failed:', err); }
  };

  const persistInsuranceProfile = async (): Promise<InsuranceProfileUpdate | null> => {
    const company = companies.find((entry: any) => String(entry.company_id) === String(insuranceCompanyId));
    const cleanInsuranceNumber = insuranceNumber.trim();
    const coverage = Number(insuranceCoveragePercentage);
    if (!company) { setError('يرجى اختيار شركة التأمين'); return null; }
    if (!cleanInsuranceNumber) { setError('يرجى إدخال رقم/معرّف التأمين للمريض'); return null; }
    if (insuranceCoveragePercentage === null || !Number.isFinite(coverage) || coverage < 0 || coverage > 100) { setError('يرجى إدخال نسبة تغطية صحيحة من 0 إلى 100'); return null; }
    if (!patientId || !effectiveDoctorId || !effectiveClinicId) { setError('تعذر تحديد ملف المريض أو الطبيب أو العيادة'); return null; }
    const update: InsuranceProfileUpdate = {
      insurance_company_id: String(company.company_id),
      insurance_company_name: String(company.name_ar || company.name_en || ''),
      insurance_number: cleanInsuranceNumber,
      coverage_percentage: coverage,
    };
    const profileUnchanged = String(patient?.insurance_company_id || '') === update.insurance_company_id &&
      String(patient?.insurance_number || '').trim() === update.insurance_number &&
      Number(patient?.coverage_percentage ?? 0) === update.coverage_percentage;
    if (!navigator.onLine && cacheOwner()) return update;
    if (profileUnchanged) return update;
    const fileNumber = String(patient?.file_number || patient?.file_id || '').trim();
    const url = fileNumber
      ? `${API.BASE_URL}/api/patients/by-file/${encodeURIComponent(fileNumber)}/info`
      : `${API.BASE_URL}/api/patients/by-id/${encodeURIComponent(patientId)}/info`;
    const params = fileNumber ? { clinic_id: effectiveClinicId } : { doctor_id: effectiveDoctorId };
    setProfileSaving(true); setError('');
    try {
      await axios.put(url, update, { params });
      setInsuranceCompanyId(update.insurance_company_id);
      setInsuranceNumber(update.insurance_number);
      setInsuranceCoveragePercentage(update.coverage_percentage);
      onPatientInsuranceUpdated?.(update);
      // MongoDB is authoritative and must finish before applying the financial
      // program. The Firestore waiting-list mirror is non-financial and should
      // not hold the save dialog open.
      void mirrorInsuranceProfile(update);
      return update;
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'تعذر حفظ بيانات التأمين للمريض');
      return null;
    } finally { setProfileSaving(false); }
  };

  const handleSave = async () => {
    if (!selections.length) { setError('يرجى اختيار بند التأمين الجديد المراد إضافته'); return; }
    const summary = priceLists.find((list: any) => String(list.effectiveDate || list.year) === String(selectedPeriod));
    if (!summary?.priceListId) { setError('تعذر تحديد قائمة أسعار التأمين'); return; }
    const profileUpdate = await persistInsuranceProfile();
    if (!profileUpdate) return;
    const appendPurchase = !serverBilling;
    const fingerprint = JSON.stringify({ selectedPeriod, selections, programVersion, billing: serverBilling?.billing_id || '', appendPurchase });
    if (!operationRef.current || operationRef.current.fingerprint !== fingerprint) operationRef.current = { fingerprint, id: crypto.randomUUID() };
    setSaving(true); setError('');
    try {
      const response = await axios.post(`${API.BASE_URL}/api/insurance/programs/apply`, {
        ...(!navigator.onLine && cacheOwner() ? { offline_profile_update: profileUpdate } : {}),
        operation_id: operationRef.current.id, source_portal: 'doctor', patient_id: patientId, patient_name: patient.patient_name,
        patient_phone: patient.patient_phone || '', patient_file_number: patient.file_number || patient.file_id || '',
        doctor_id: effectiveDoctorId, catalog_doctor_id: catalogDoctorId, clinic_id: effectiveClinicId,
        insurance_company_id: insuranceCompanyId, coverage_percentage: coveragePercentage,
        insurance_price_list_id: summary.priceListId, billing_id: serverBilling?.billing_id || '', new_billing_id: crypto.randomUUID(),
        expected_program_version: programVersion, expected_billing_version: serverBilling ? Number(serverBilling.version || 0) : undefined,
        append_purchase: appendPurchase,
        // Existing session trackers are not proof of an existing charge. The
        // authoritative endpoint already reuses the program billing and guards
        // concurrent/idempotent writes, so never suppress the patient's due bill.
        tracking_only: false, queue_entry_id: patientId, queue_date: String(patient.date || '').slice(0, 10), selections,
      });
      if (!response.data?.success) throw new Error(response.data?.message || 'تعذر حفظ برنامج التأمين');
      const savedResult = response.data.data;
      // The financial transaction has committed. Reflect its authoritative
      // response immediately, then refresh mirrors/caches without delaying UX.
      void Promise.resolve().then(() => onSaved(savedResult)).catch((refreshError) => {
        console.warn('Insurance program saved; background refresh failed:', refreshError);
      });
      void mirrorWaitingList(savedResult);
      message.success(response.data.idempotent ? 'تم تأكيد العملية السابقة بدون تكرار' : 'تم حفظ برنامج التأمين بالقيمة المعتمدة');
      operationRef.current = null; onClose();
    } catch (err: any) {
      if (isProfileQueued(err)) { message.warning('Insurance request saved on this device — prices and versions will be verified during sync.'); onClose(); return; }
      setError(err?.response?.data?.code === 'OPEN_INSURANCE_BILL_EXISTS'
        ? 'توجد فاتورة تأمين غير مدفوعة لهذا المريض. أغلق النافذة وافتحها مرة أخرى لتعديلها بدل إنشاء فاتورة جديدة.'
        : err?.response?.status === 409 ? (err?.response?.data?.message || 'تم تعديل البيانات من بوابة أخرى؛ أعد التحميل') :
          err?.response?.data?.message || err?.message || 'تعذر حفظ برنامج التأمين');
    } finally { setSaving(false); }
  };

  return <Modal title={`برنامج التأمين — ${patient?.patient_name || ''}`} open={open} onCancel={onClose} width={860} destroyOnClose
    styles={{ body: { maxHeight: '72vh', overflowY: 'auto' } }}
    footer={[
      <Button key="cancel" onClick={onClose} disabled={saving || profileSaving}>إلغاء</Button>,
      <Button key="save" type="primary" onClick={handleSave} loading={saving || profileSaving} disabled={loading || !insuranceCompanyId || insuranceCoveragePercentage === null || !insuranceNumber.trim() || !selections.length}>
        {canManageOwnerFeatures
          ? `حفظ البرنامج بالقيمة المعتمدة (${previewTotal.toLocaleString('ar-EG')} ج)`
          : 'حفظ برنامج التأمين'}
      </Button>,
    ]}>
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={canManageOwnerFeatures
          ? 'اختر الشركة وأدخل نسبة التغطية يدويًا. تُعرض الأسعار للمعاينة، ويعيد الخادم حساب كل مبلغ من قائمة الأسعار المعتمدة قبل الحفظ.'
          : 'اختر الشركة وأدخل نسبة التغطية، ثم حدد الخدمات وعدد الجلسات.'}
      />
      {error && <Alert type="error" showIcon message={error} />}
      <Text strong>شركة التأمين</Text>
      <Select
        showSearch
        optionFilterProp="children"
        loading={companiesLoading}
        placeholder="اختر شركة التأمين"
        value={insuranceCompanyId || undefined}
        onChange={(value) => {
          setInsuranceCompanyId(String(value));
          setInsuranceCoveragePercentage(String(value) === String(patient?.insurance_company_id || '') ? Number(patient?.coverage_percentage || 0) : null);
          setError('');
        }}
        style={{ width: '100%' }}
      >
        {companies.map((company: any) => <Select.Option key={company.company_id} value={company.company_id}>
          {company.name_ar || company.name_en}{company.name_ar && company.name_en ? ` — ${company.name_en}` : ''}
        </Select.Option>)}
      </Select>
      <Space wrap size="middle" style={{ width: '100%' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Text strong>نسبة تغطية الشركة % (يدوي)</Text>
          <InputNumber min={0} max={100} precision={2} value={insuranceCoveragePercentage} onChange={(value) => setInsuranceCoveragePercentage(value === null ? null : Number(value))} placeholder="مثال: 80" style={{ width: '100%', marginTop: 6 }} />
        </div>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Text strong>رقم/معرّف التأمين</Text>
          <Input value={insuranceNumber} onChange={(event) => setInsuranceNumber(event.target.value)} placeholder="رقم عضوية التأمين" maxLength={120} style={{ marginTop: 6 }} />
        </div>
      </Space>
      {!insuranceCompanyId ? <Alert type="warning" showIcon message="اختر شركة التأمين لعرض قائمة أسعار أنواع الزيارات والخدمات." /> : loading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : <>
      <Text strong>{canManageOwnerFeatures ? 'قائمة أسعار التأمين' : 'خدمات التأمين والجلسات'}</Text>
      {!serverBilling && relevantActivePackages.length > 0 && (
        <Alert type="success" showIcon message="الجلسات السابقة محفوظة ولن تُضاف إلى الإجمالي. اختر الإضافة الجديدة فقط." />
      )}
      {priceLists.length > 1 && <Select value={selectedPeriod} onChange={(period) => { setSelectedPeriod(period); setSelected({}); setPriceList(null); }} style={{ width: '100%' }}>{priceLists.map((list: any) => { const period = String(list.effectiveDate || list.year); return <Select.Option key={list.priceListId || period} value={period}>{list.effectiveDate || list.year}</Select.Option>; })}</Select>}
      {!insuranceItems.length && !error ? <Alert type="info" showIcon message="لا توجد أسعار تأمين مضافة لهذه الشركة" /> : <List bordered dataSource={insuranceItems} renderItem={(item: any) => {
        const key = itemKey(item); const quantity = selected[key] || 0; const full = Number(item.insurancePrice || 0);
        const existingPackage = relevantActivePackages.find((pkg: any) => String(pkg.source_item_id || '') === String(itemId(item)));
        const isAddingToExistingPackage = !serverBilling && Boolean(existingPackage);
        const existingTotal = Number(existingPackage?.total_sessions || 0);
        const existingRemaining = Number(existingPackage?.remaining_sessions || 0);
        const patientPrice = coveragePercentage > 0 ? Math.round((full * (100 - coveragePercentage)) / 100) : full;
        const type = item.itemType === 'service' ? 'خدمة' : item.itemType === 'custom' ? 'مخصص' : 'نوع زيارة';
        const color = item.itemType === 'service' ? 'green' : item.itemType === 'custom' ? 'purple' : 'blue';
        return <List.Item><Space direction="vertical" style={{ width: '100%' }}><Space wrap>
          <Checkbox checked={quantity > 0} onChange={(event) => setSelected((current) => { const next = { ...current }; if (event.target.checked) next[key] = next[key] || 1; else delete next[key]; return next; })}><Text strong>{itemName(item)}</Text></Checkbox><Tag color={color}>{type}</Tag>
          {!serverBilling && existingPackage && <Tag color="cyan">رصيد سابق: {existingPackage.remaining_sessions ?? 0} / {existingPackage.total_sessions ?? 0} — غير محسوب</Tag>}
          {canManageOwnerFeatures && <><Text type="secondary">قبل التأمين: {full.toLocaleString('ar-EG')} ج</Text><Text type="success">المريض يدفع: {patientPrice.toLocaleString('ar-EG')} ج</Text></>}</Space>
          {quantity > 0 && <Space wrap>
            <Text>{isAddingToExistingPackage ? 'عدد الجلسات الجديدة:' : 'عدد الجلسات:'}</Text>
            <InputNumber min={1} max={10000} precision={0} value={quantity} onChange={(value) => setSelected((current) => ({ ...current, [key]: Number(value || 1) }))}/>
            {isAddingToExistingPackage && (
              <Tag color="blue">بعد الإضافة: {existingRemaining + quantity} / {existingTotal + quantity}</Tag>
            )}
            {canManageOwnerFeatures && <Text strong>المطلوب الآن: {(patientPrice * quantity).toLocaleString('ar-EG')} ج</Text>}
          </Space>}
        </Space></List.Item>;
      }}/>} {/* End insurance item list */}</>}
    </Space>
  </Modal>;
};
export default InsuranceProgramModal;
